import React, { useEffect, useState } from 'react';
import { fetchAIData } from '../../../../../platform/custom/Apis/apis';
import { addImage, removeImage } from '@custom/store/slices/report/imageSelectionSlice';
import { removeAIImage, addAIImage } from '@custom/store/slices/report/aiImageSelectionSlice';
import { addImageforDwnld, removeImageforDwnld } from '@custom/store/slices/DCM4CHEE/dwnldImgSlice';
import { Tooltip } from '@ohif/ui';
import classnames from 'classnames';
import './ViewportOverlay.css';
import { servicesManager } from '../../../../app/src/App';
import Icon from '../Icon';
import hospitalDetailApi from '@custom/rtk-apis/hospital/hospitalDetails';
import userDetails from '@custom/rtk-apis/user/userDetails';
import DraggableModal from './DraggableModal';
import BoneXrayModal from './BoneXrayModal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@custom/store/store';

// The overlay-top and overlay-bottom classes are explicitly needed to offset
// the overlays (i.e. via absolute positioning) such the ViewportActionCorners
// have space for its child components.
// ToDo: offset the ViewportOverlay automatically via css to account for the
// space needed for ViewportActionCorners.
const classes = {
  topLeft: 'overlay-top left-viewport',
  topRight: 'overlay-top right-viewport-scrollbar',
  bottomRight: 'overlay-bottom right-viewport-scrollbar',
  bottomLeft: 'overlay-bottom left-viewport',
};

export type ViewportOverlayProps = {
  topLeft: React.ReactNode;
  topRight: React.ReactNode;
  bottomRight: React.ReactNode;
  bottomLeft: React.ReactNode;
  getCurrImageId: () => string;
  captureImgWithAnnot: () => Promise<string>;
  color?: string;
  element?: HTMLElement;
};

const ViewportOverlay = ({
  topLeft,
  topRight,
  bottomRight,
  bottomLeft,
  getCurrImageId,
  captureImgWithAnnot,
  color = 'text-primary-light',
  element
}: ViewportOverlayProps) => {
  const overlay = 'absolute pointer-events-none viewport-overlay';
  const [currentSOPInstanceUID, setCurrentSOPInstanceUID] = useState<string | null>(null);
  const [showSOPModal, setShowSOPModal] = useState(false);
  const [aiData, setAiData] = useState<{ isLoading: boolean | null; data: Record<string, any> | null }>({ isLoading: null, data: null });
  const { viewportGridService, cornerstoneViewportService } = servicesManager.services;
  const dispatch = useDispatch();
  const [aiServiceAvailable, setAiServiceAvailable] = useState(false);

  const currentUrl = window.location.href;
  const url = new URL(currentUrl);
  const studyInstanceUID = url.searchParams.get("StudyInstanceUIDs");

  // Selectors with proper typing
  const reportImageSelector = useSelector((state: any) => state.reportImageSelector);
  const aiImageSelector = useSelector((state: any) => state.aiImageSelector);
  const dwnldImgIds = useSelector((state: any) => state.dwnldImgIds);
  const { result: aiStudyResult } = useSelector((state: RootState) => state.getAIStudiesResponse);
  const hospitalDetail = useSelector((state: RootState) => state.hospitalDetailsResponse);

  const aiStudiesFlat = Array.isArray(aiStudyResult)
    ? aiStudyResult.flatMap((d: any) => d?.studies || [])
    : [];
  const currentAIStudy = aiStudiesFlat.find((s: any) => s?.studyInstanceUID === studyInstanceUID);
  console.log("currentAIStudy", currentAIStudy);


  // Function to extract SOP Instance UID and Series Instance UID from image ID
  const extractSOPInstanceUID = (imageId: string): { sopInstanceUID: string | null; seriesInstanceUID: string | null } => {
    if (!imageId) return { sopInstanceUID: null, seriesInstanceUID: null };
    // Format: 'wadouri:http://.../studies/1.2.3.4.5/series/2.3.4.5.6/instances/1.2.3.4.5.6.7.8.9.10'
    const parts = imageId.split('/');
    let sopInstanceUID = null;
    let seriesInstanceUID = null;

    for (let i = 0; i < parts.length; i++) {
      if (parts[i] === 'series' && i + 1 < parts.length) {
        seriesInstanceUID = parts[i + 1];
      } else if (parts[i] === 'instances' && i + 1 < parts.length) {
        sopInstanceUID = parts[i + 1].split('/')[0]; // In case there are more parts after the UID
      }
    }

    return { sopInstanceUID, seriesInstanceUID };
  };

  // Update SOP Instance UID when image changes
  useEffect(() => {
    if (!viewportGridService || !cornerstoneViewportService) return;

    const updateSOPInstanceUID = () => {
      try {
        const viewportId = viewportGridService.getActiveViewportId();
        if (!viewportId) return;

        const viewport = cornerstoneViewportService.getCornerstoneViewport(viewportId);
        if (!viewport) return;

        // Get the current image ID using the prop function if available, otherwise try viewport method
        let imageId: string | undefined;
        if (typeof getCurrImageId === 'function') {
          imageId = getCurrImageId();
        } else if (typeof (viewport as any).getCurrentImageId === 'function') {
          imageId = (viewport as any).getCurrentImageId();
        } else if (typeof (viewport as any).getCurrentImageIdIndex === 'function') {
          // Fallback to getting image by index if getCurrentImageId is not available
          const index = (viewport as any).getCurrentImageIdIndex();
          const imageIds = (viewport as any).getImageIds?.();
          if (imageIds && index >= 0 && index < imageIds.length) {
          }
        }

        if (!imageId) return;

        const { sopInstanceUID, seriesInstanceUID } = extractSOPInstanceUID(imageId);
        if (sopInstanceUID && sopInstanceUID !== currentSOPInstanceUID) {
          setCurrentSOPInstanceUID(sopInstanceUID);
        }
      } catch (error) {
        console.error('Error updating SOP Instance UID:', error);
      }
    };

    // Initial update
    updateSOPInstanceUID();

    // Subscribe to viewport changes if available
    let unsubscribe: (() => void) | undefined;
    if (viewportGridService.EVENTS?.ACTIVE_VIEWPORT_ID_CHANGED) {
      const subscription = viewportGridService.subscribe(
        viewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED,
        updateSOPInstanceUID
      );
      if (subscription?.unsubscribe) {
        unsubscribe = subscription.unsubscribe;
      }
    }

    // Set up an interval to check for image changes
    const intervalId = setInterval(updateSOPInstanceUID, 100);

    return () => {
      clearInterval(intervalId);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentSOPInstanceUID, viewportGridService, cornerstoneViewportService, getCurrImageId]);

  const { uiNotificationService } = servicesManager.services;

  const isImageChecked = () => {
    return reportImageSelector?.selectedImages?.some(image => image.imgName === getCurrImageId());
  };

  const handleCheckboxChange = async (event: React.MouseEvent<HTMLInputElement>) => {

    if (isImageChecked()) {
      dispatch(removeImage(getCurrImageId()));
    }
    else {
      dispatch(addImage({
        imgName: getCurrImageId(),
        imgData: await captureImgWithAnnot()
      }))
    }
  }
  const handleImageSelectionForDwnld = (): void => {
    if (isImageSelectedForDwnld()) {
      dispatch(removeImageforDwnld(getCurrImageId()));
    }
    else {
      dispatch(addImageforDwnld(getCurrImageId()));
    }
  }

  const isImageSelectedForDwnld = () => {
    return dwnldImgIds?.selectedImages?.some(image => image === getCurrImageId());
  };


  const handleFullScreen = () => {
    if (element) {
      // Check if the browser supports Fullscreen API
      const requestFullscreen =
        (element as any).requestFullscreen ||
        (element as any).webkitRequestFullscreen ||
        (element as any).msRequestFullscreen ||
        (element as any).mozRequestFullScreen;

      if (requestFullscreen) {
        requestFullscreen
          .call(element)
          .then(() => {
            console.log('Entered fullscreen mode successfully');
          })
          .catch(err => {
            console.error('Failed to enter fullscreen mode:', err);
            uiNotificationService.show({
              title: 'Fullscreen Error',
              message: 'Unable to enter full screen mode. Please try again.',
              type: 'error',
            });
          });
      } else {
        console.error('Fullscreen API is not supported in this browser.');
        uiNotificationService.show({
          title: 'Fullscreen Error',
          message: 'Fullscreen mode is not supported on your device/browser.',
          type: 'error',
        });
      }
    } else {
      console.error('No element to enter fullscreen mode.');
    }
  };

  const exitFullScreen = () => {
    if ((document as any).exitFullscreen) {
      (document as any).exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      (document as any).mozCancelFullScreen();
    }
  };

  const toggleFullScreen = (e: React.MouseEvent) => {
    if (!document.fullscreenElement) {
      handleFullScreen();
    } else {
      exitFullScreen();
    }
  };

  const getFrameAIData = async () => {
    const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');


    if (!hospitalId || !studyInstanceUID) {
      console.error("Missing hospitalId or studyInstanceUID");
      setAiData({ isLoading: false, data: null });
      return null;
    }

    setAiData({ isLoading: true, data: null });

    try {
      const seriesResults = await fetchAIData(hospitalId, studyInstanceUID);
      console.log("AI Data response", seriesResults);
      setAiData({ isLoading: false, data: seriesResults });
      return seriesResults;
    } catch (error) {
      console.error("Error fetching AI data:", error);
      setAiData({ isLoading: false, data: null });
      throw error;
    }
  }


  useEffect(() => {
    const checkAiServiceAvailability = async () => {
      try {
        const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
        const [hospitalDetail, userDetail] = await Promise.all([
          dispatch(hospitalDetailApi({ hospitalId })).unwrap(),
          dispatch(userDetails()).unwrap()
        ]);
        setAiServiceAvailable(
          hospitalDetail?.result?.addOnsAvailable?.aiServices === true &&
          userDetail?.result?.aiTraining === true
        );

        //If the aiServiceAvailable is true, then call the getFrameAIData function
        if (hospitalDetail?.result?.addOnsAvailable?.aiServices === true) {
          getFrameAIData();
        }
      } catch (err) {
        console.error("Error in checkAiServiceAvailability", err);
        setAiServiceAvailable(false);
      }
    };
    checkAiServiceAvailability();
  }, [dispatch])

  const acceptedModalityandStudyRegion = () => {
    // this funtion allow to show AI button only for XRAY and CT modalities and CHEST, BONE, BRAIN study regions also hospital have AI plan
    const modalities = ["XRAY", "CT"];
    const studyRegions = ["BONE", "BRAIN"];
    const hospitalhasAIPlan = hospitalDetail?.result?.addOnsAvailable?.aiServices === true;

    const isAllow = modalities.includes(currentAIStudy?.modality) &&
      studyRegions.includes(currentAIStudy?.studyRegion) && currentAIStudy?.status === "COMPLETED" && hospitalhasAIPlan;

    return isAllow;
  };

  return (
    <div
      className={classnames(
        color ? color : 'text-aqua-pale',
        'text-[13px]',
        'leading-5',
        'overlay-text'
      )}
    >
      <div
        data-cy={'viewport-overlay-top-left'}
        className={classnames(overlay, classes.topLeft)}
      >
        {topLeft}
      </div>
      <div
        data-cy={'viewport-overlay-top-right'}
        className={classnames(overlay, classes.topRight)}
        style={{ transform: 'translateX(-8px)' }} // shift right side overlays by 4px for better alignment with ViewportActionCorners' icons
      >
        <div
          onClick={e => toggleFullScreen(e)}
          className="z-1000"
          style={{ zIndex: '10000', pointerEvents: 'auto', filter: 'invert(1)' }}
        >
          <Icon
            name="maximize-icon"
            style={{ color: 'white' }}
          />
        </div>
        {topRight}
      </div>
      <div
        data-cy={'viewport-overlay-bottom-right'}
        className={classnames(overlay, classes.bottomRight)}
        style={{ transform: 'translateX(-8px)' }} // shift right side overlays by 4px for better alignment with ViewportActionCorners' icons
      >
        <div style={{ position: 'relative', pointerEvents: 'auto' }}>
          {showSOPModal && currentSOPInstanceUID && (
            // Conditionally render BoneXrayModal for XRAY-BONE studies, otherwise use DraggableModal
            currentAIStudy?.modality === 'XRAY' && currentAIStudy?.studyRegion === 'BONE' ? (
              <BoneXrayModal
                onClose={() => setShowSOPModal(false)}
                sopInstanceUID={currentSOPInstanceUID}
                seriesInstanceUID={extractSOPInstanceUID(getCurrImageId() || '').seriesInstanceUID}
                aiData={aiData.data}
              />
            ) : (
              <DraggableModal
                onClose={() => setShowSOPModal(false)}
                sopInstanceUID={currentSOPInstanceUID}
                seriesInstanceUID={extractSOPInstanceUID(getCurrImageId() || '').seriesInstanceUID}
                aiData={aiData.data}
              />
            )
          )}
          {aiData?.isLoading === false && acceptedModalityandStudyRegion() && (<button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowSOPModal(!showSOPModal);
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            style={{
              position: 'absolute',
              top: '-40px',
              left: '10px',
              background: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              border: '1px solid #4a90e2',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '12px',
              cursor: 'pointer',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              pointerEvents: 'auto',
              outline: 'none',
              WebkitTapHighlightColor: 'transparent'
            }}
          >
            <span>SOP UID</span>
            <Icon name="info-link" style={{ width: '12px', height: '12px' }} />
          </button>)}
        </div>
        {
          reportImageSelector?.showSelectionCheckbox ?
            <Tooltip content={'Select Image for Report'} position='left' className={`inline-block`}>
              <input style={{ zIndex: '1000', pointerEvents: 'auto' }} type="checkbox"
                checked={isImageChecked()} onClick={handleCheckboxChange} />
            </Tooltip>
            : null
        }
        {bottomRight}
      </div>
      <div
        data-cy={'viewport-overlay-bottom-left'}
        className={classnames(overlay, classes.bottomLeft)}
      >
        <Tooltip content={'Select Image for Download'} position='right' className={`inline-block`}>
          <input style={{ zIndex: '1000', pointerEvents: 'auto' }} type="checkbox"
            checked={isImageSelectedForDwnld()} onClick={handleImageSelectionForDwnld} />
        </Tooltip>
        {bottomLeft}
      </div>
    </div>
  );
};

export default ViewportOverlay;
